import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { api } from './services/api';
import { useAuth } from './contexts/AuthContext';
import { useLanguage } from './contexts/LanguageContext';
import { foldersService } from './services/folders.service';
import { alpha } from './themes/brand';
import { secoesVisiveis } from './navegacao/secoes';

interface Rule {
  _id: string;
  name: string;
  prompt: string;
  description?: string;
}

interface FolderData {
  id: string;
  name: string;
  description?: string;
  fileCount?: number;
}

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px;
  animation: fadeIn 0.5s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @media (max-width: 768px) {
    padding: 16px;
    gap: 24px;
  }
`;

/* ================================================================================================
   Atalhos das seções — os "botões flutuantes" da tela inicial.

   As seções vêm de `navegacao/secoes.ts`, a MESMA fonte que a barra lateral usa. Duplicar a lista
   aqui criaria a duplicação que divergindo não quebra: uma seção nova apareceria só num dos lugares,
   e um atalho ficaria visível para quem perdeu acesso — sem erro e sem sintoma até alguém clicar.

   `button` e não `div`: é um controle que navega. Como `div` ele não recebe foco, não responde a
   Enter e não é anunciado como acionável — teclado e leitor de tela ficariam de fora.
   ================================================================================================ */

const AtalhosSecao = styled.section`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AtalhosTitulo = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: ${({ theme }) => theme.title};
`;

const AtalhosSubtitulo = styled.p`
  margin: 0 0 20px;
  font-size: 14px;
  color: ${({ theme }) => theme.textSecondary};
`;

/*
  `auto-fit` com `minmax` em vez de número fixo de colunas: a grade se reorganiza sozinha conforme a
  largura, de 1 coluna em janela estreita a 4 em monitor largo, sem media query por faixa. Menos
  código e nenhuma largura em que o layout fica quebrado entre dois breakpoints.
*/
const AtalhosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(232px, 1fr));
  gap: 16px;
`;

const AtalhoBotao = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 20px;
  text-align: left;
  cursor: pointer;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.cardBorder};
  background: ${({ theme }) => theme.card};
  /* A sombra é o que faz o botão "flutuar" — sutil em repouso para não competir com o conteúdo. */
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 8px 24px -16px rgba(0, 0, 0, 0.18);
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ theme }) => theme.primaryAccent};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05), 0 16px 32px -18px rgba(0, 0, 0, 0.26);
  }

  &:active {
    transform: translateY(0);
  }

  /* Foco visível: sem isto o atalho fica inutilizável por teclado, mesmo sendo um button. */
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primaryAccent};
    outline-offset: 2px;
  }

  /* Quem pediu para reduzir movimento não recebe o salto no hover. */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }
`;

const AtalhoIcone = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  color: ${({ theme }) => theme.primaryAccent};
  background: ${({ theme }) => theme.buttonColorProfile};
`;

const AtalhoRotulo = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.title};
`;

const AtalhoDescricao = styled.span`
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.textSecondary};
`;

const AgentsPanel = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const AgentsTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
  letter-spacing: -0.02em;
`;

const AgentsSubtitle = styled.p`
  margin: -16px 0 0 0;
  color: ${({ theme }) => theme.textSecondary || theme.text};
  opacity: 0.7;
  font-size: 15px;
  line-height: 1.5;
`;

const AgentsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
`;

const AgentCard = styled.button<{ $selected?: boolean }>`
  position: relative;
  background: ${({ $selected, theme }) =>
    $selected ? theme.card + 'ee' : theme.card + 'bb'};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid ${({ $selected, theme }) =>
    $selected ? theme.primary : theme.border};
  border-radius: 20px;
  padding: 24px;
  text-align: left;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${({ theme }) => theme.text};
  height: 190px;
  box-shadow: ${({ $selected, theme }) =>
    $selected ? `0 0 0 2px ${theme.primary}40, 0 8px 20px -8px ${theme.primary}50` : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'};
  overflow: hidden;

  /* Subtle inner glow */
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    border-radius: 20px;
    box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.05);
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-6px) scale(1.02);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1), 0 0 20px ${({ theme }) => theme.primary}30;
    border-color: ${({ theme }) => theme.primaryHover};
    background: ${({ theme }) => theme.card}f2;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary};
    outline-offset: 2px;
  }
`;

const AgentBadge = styled.div`
  align-self: flex-start;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  /* \`primaryAccent\`, não \`primary\`: aqui a cor de marca é TEXTO sobre superfície, e petrol
     puro sobre fundo escuro dá 1.81:1. O accent é o tint que passa AA nos dois temas. */
  color: ${({ theme }) => theme.primaryAccent};
  background-color: ${({ theme }) => theme.primaryAccent}15;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.primaryAccent}30;
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.primaryOn};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  animation: popIn 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  @keyframes popIn {
    from { transform: scale(0); }
    to { transform: scale(1); }
  }
`;

const AgentName = styled.h3`
  font-weight: 600;
  font-size: 17px;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AgentFooter = styled.div`
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary || theme.text};
  opacity: 0.8;
  font-weight: 500;
`;

const AgentDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.success};
  box-shadow: 0 0 0 2px ${({ theme }) => alpha(theme.success, 0.15)};
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 16px;
  position: sticky;
  bottom: 0;
  padding: 24px 0;
  z-index: 10;
`;

const PrimaryButton = styled.button`
  padding: 14px 32px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, ${({ theme }) => theme.primary}, ${({ theme }) => theme.primaryHover || theme.primary});
  color: ${({ theme }) => theme.primaryOn};
  font-weight: 600;
  font-size: 15px;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  min-width: 160px;
  box-shadow: 0 4px 12px ${({ theme }) => theme.primary}40, inset 0 1px 1px rgba(255, 255, 255, 0.2);

  &:hover {
    filter: brightness(1.1);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px ${({ theme }) => theme.primary}60, inset 0 1px 1px rgba(255, 255, 255, 0.3);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 2px 4px ${({ theme }) => theme.primary}40;
  }

  &:disabled {
    background: ${({ theme }) => theme.borderLight || theme.border};
    color: ${({ theme }) => theme.textSecondary};
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SecondaryButton = styled.button`
  padding: 14px 28px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.borderLight || theme.border};
  background-color: ${({ theme }) => theme.card}bb;
  backdrop-filter: blur(8px);
  color: ${({ theme }) => theme.text};
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => theme.background};
    border-color: ${({ theme }) => theme.primary};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
`;

const ModalContainer = styled.div`
  background-color: ${({ theme }) => theme.card}ee;
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid ${({ theme }) => theme.borderLight || theme.border};
  border-radius: 24px;
  padding: 32px;
  width: 540px;
  max-width: 95%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4), inset 0 1px 1px rgba(255,255,255,0.05);
  animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(30px) scale(0.95); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
`;

const ModalText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.textSecondary || theme.text};
  opacity: 0.8;
  line-height: 1.5;
`;

const FolderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 4px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.border};
    border-radius: 3px;
  }
`;

const FolderItem = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.border};
  cursor: pointer;
  color: ${({ theme }) => theme.text};
  background-color: ${({ theme }) => theme.background};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    background-color: ${({ theme }) => theme.sidebarItemHover};
  }

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    accent-color: ${({ theme }) => theme.primary};
  }
`;

const FolderName = styled.span`
  font-weight: 500;
  font-size: 14px;
`;

const LoadingText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  font-size: 14px;
  font-style: italic;
`;

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, hasAdminAccess, isManager } = useAuth();
  const { t } = useLanguage();

  /**
   * Atalhos exibidos na tela inicial.
   *
   * Vêm de `secoesVisiveis`, a mesma função que a barra lateral usa — então as regras de permissão
   * são as dela, não uma segunda cópia. Um atalho para uma tela que o usuário não pode abrir não é
   * detalhe estético: é promessa falsa.
   *
   * `dashboard` é removido de propósito: esta É a tela do dashboard, e atalho para a página em que a
   * pessoa já está é ruído — ocupa um cartão e não leva a nada.
   */
  const atalhos = useMemo(
    () =>
      secoesVisiveis({ admin: hasAdminAccess(), manager: isManager() })
        .filter(secao => secao.chave !== 'dashboard'),
    [hasAdminAccess, isManager],
  );
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folders, setFolders] = useState<FolderData[]>([]);
  const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [foldersError, setFoldersError] = useState('');

  useEffect(() => {
    const fetchRules = async () => {
      if (!user || !user.organizationId) return;
      setLoading(true);
      setLoadError('');

      try {
        const token = localStorage.getItem('@App:token');
        const organizationId = Array.isArray(user.organizationId)
          ? user.organizationId[0]
          : user.organizationId;

        const response = await api.get(`/api/prompt/organization/${organizationId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        const formattedRules = (response.data || []).map((rule: any) => ({
          _id: rule._id || rule.id,
          name: rule.name,
          prompt: rule.prompt || '',
          description: rule.description || ''
        }));

        setRules(formattedRules);
      } catch (error) {
        console.error('Erro ao carregar prompts:', error);
        setLoadError('Não foi possível carregar os agentes agora.');
        setRules([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRules();
  }, [user]);

  const agentCards = useMemo(() => {
    return rules.map(rule => ({
      ...rule,
      displayName: `Agente ${rule.name}`
    }));
  }, [rules]);

  const toggleAgentSelection = (agentId: string) => {
    setSelectedAgentIds(prev =>
      prev.includes(agentId) ? prev.filter(id => id !== agentId) : [...prev, agentId]
    );
  };

  const openFolderSelection = async () => {
    setShowFolderModal(true);
    setFoldersError('');
    setLoadingFolders(true);

    try {
      const response = await foldersService.getMyFolders();
      setFolders(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar pastas:', error);
      setFoldersError('Não foi possível carregar as pastas.');
      setFolders([]);
    } finally {
      setLoadingFolders(false);
    }
  };

  const toggleFolderSelection = (folderId: string) => {
    setSelectedFolderIds(prev =>
      prev.includes(folderId) ? prev.filter(id => id !== folderId) : [...prev, folderId]
    );
  };

  const buildChatUrl = (folderIds: string[] = []) => {
    const promptIdsParam = selectedAgentIds.join(',');
    const folderIdsParam = folderIds.join(',');
    const query = folderIdsParam
      ? `?promptIds=${encodeURIComponent(promptIdsParam)}&folderIds=${encodeURIComponent(folderIdsParam)}&full=true`
      : `?promptIds=${encodeURIComponent(promptIdsParam)}&full=true`;

    return `/compliance/pending${query}`;
  };

  const startChatWithoutFolders = () => {
    navigate(buildChatUrl([]));
    setShowFolderModal(false);
  };

  const startChatWithFolders = () => {
    navigate(buildChatUrl(selectedFolderIds));
    setShowFolderModal(false);
  };

  return (
    <DashboardContainer>
      <AtalhosSecao>
        <AtalhosTitulo>{t('home.titulo')}</AtalhosTitulo>
        <AtalhosSubtitulo>{t('home.subtitulo')}</AtalhosSubtitulo>
        <AtalhosGrid>
          {atalhos.map(secao => (
            <AtalhoBotao
              key={secao.chave}
              type="button"
              onClick={() => secao.navegar(navigate)}
              aria-label={t(secao.rotulo)}
            >
              <AtalhoIcone aria-hidden="true">{secao.icone(24)}</AtalhoIcone>
              <AtalhoRotulo>{t(secao.rotulo)}</AtalhoRotulo>
              <AtalhoDescricao>{t(secao.descricao)}</AtalhoDescricao>
            </AtalhoBotao>
          ))}
        </AtalhosGrid>
      </AtalhosSecao>

      <AgentsPanel>
        <AgentsTitle>Agentes Especialistas</AgentsTitle>
        <AgentsSubtitle>Escolha um agente para iniciar o chat.</AgentsSubtitle>
        {loading ? (
          <LoadingText>Carregando agentes...</LoadingText>
        ) : loadError ? (
          <LoadingText>{loadError}</LoadingText>
        ) : agentCards.length === 0 ? (
          <LoadingText>Nenhum agente disponível no momento.</LoadingText>
        ) : (
          <AgentsList>
            {agentCards.map(rule => (
              <AgentCard
                key={rule._id}
                onClick={() => toggleAgentSelection(rule._id)}
                $selected={selectedAgentIds.includes(rule._id)}
              >
                {selectedAgentIds.includes(rule._id) && (
                  <SelectedBadge>✓</SelectedBadge>
                )}
                <AgentBadge>Agente</AgentBadge>
                <AgentName className="agent-name">{rule.displayName}</AgentName>
                <AgentFooter>
                  <AgentDot />
                  Pronto para iniciar
                </AgentFooter>
              </AgentCard>
            ))}
          </AgentsList>
        )}
        <ActionRow>
          <PrimaryButton
            type="button"
            disabled={selectedAgentIds.length === 0}
            onClick={openFolderSelection}
          >
            Continuar
          </PrimaryButton>
        </ActionRow>
      </AgentsPanel>

      {showFolderModal && (
        <ModalOverlay>
          <ModalContainer>
            <ModalTitle>Selecionar pastas</ModalTitle>
            <ModalText>
              Deseja adicionar pastas de contexto? Você pode pular esta etapa.
            </ModalText>

            {loadingFolders ? (
              <LoadingText>Carregando pastas...</LoadingText>
            ) : foldersError ? (
              <LoadingText>{foldersError}</LoadingText>
            ) : folders.length === 0 ? (
              <LoadingText>Nenhuma pasta disponível.</LoadingText>
            ) : (
              <FolderList>
                {folders.map(folder => (
                  <FolderItem key={folder.id}>
                    <input
                      type="checkbox"
                      checked={selectedFolderIds.includes(folder.id)}
                      onChange={() => toggleFolderSelection(folder.id)}
                    />
                    <FolderName>{folder.name}</FolderName>
                  </FolderItem>
                ))}
              </FolderList>
            )}

            <ActionRow>
              <SecondaryButton type="button" onClick={startChatWithoutFolders}>
                Pular pastas
              </SecondaryButton>
              <PrimaryButton
                type="button"
                onClick={startChatWithFolders}
                disabled={loadingFolders}
              >
                Iniciar chat
              </PrimaryButton>
            </ActionRow>
          </ModalContainer>
        </ModalOverlay>
      )}

    </DashboardContainer>
  );
};

export default HomePage;